import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, Text, Textarea, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {useABACPolicyForm} from "./useABACPolicyForm";
import {EditorInput} from "../../editor/EditorInput/EditorInput";

export interface ABACPolicyFormProps {
  options?: ABACPolicyFormOptions;
  defaultValues?: ABACPolicyFormDefaultValues;
  onSubmit?: (values: ABACPolicyFormValues) => void;
  submitting?: boolean;
}

export interface ABACPolicyFormOptions {

}

export interface ABACPolicyFormValues {
  name: string;
  description?: string;
  expression: string;
}

export interface ABACPolicyFormDefaultValues extends Partial<ABACPolicyFormValues> {

}

export interface ABACPolicyFormRawValues {
  name?: string;
  description?: string;
  expression?: string;
}

export type ABACPolicyFormType = UseFormReturnType<ABACPolicyFormRawValues, (values: ABACPolicyFormRawValues) => ABACPolicyFormRawValues>

export const ABACPolicyForm = (props: ABACPolicyFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useABACPolicyForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {

    if (onSubmit) {
      onSubmit({
        name: values.name!,
        description: values.description,
        expression: values.expression!,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.abacpolicy.form.field.name", "Name")}
            placeholder={t<string>("common.entity.abacpolicy.form.field.name.placeholder", "Name")}
            withAsterisk
            required
            {...form.getInputProps("name")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label={t("common.entity.abacpolicy.form.field.description", "Description")}
            placeholder={t<string>("common.entity.abacpolicy.form.field.description.placeholder", "Description")}
            {...form.getInputProps("description")} />
        </Grid.Col>
        <Divider my="sm" variant="dotted"/>
        <Grid.Col span={12}>
          <Text weight={500} size="sm" mb="xs">
            {t("common.entity.abacpolicy.form.field.expression", "Expression (JSON Logic)")}
          </Text>
          <EditorInput
            height="300px"
            defaultLanguage="json"
            value={form.values.expression}
            onChange={(value) => form.setFieldValue('expression', value || '')}
            options={{minimap: {enabled: false}, formatOnPaste: true, formatOnType: true}}/>
          {form.errors.expression && (
            <Text color="red" size="xs" mt="xs">
              {form.errors.expression}
            </Text>
          )}
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface ABACPolicyFormModalProps extends ABACPolicyFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ABACPolicyFormModal = (props: ABACPolicyFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ABACPolicyForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
